import { omit, times } from 'lodash'
import { Reducer } from 'react'
import { MAX_NUMBER_OF_GUESSES } from '../constants/constants'
import { createDifficulty, createId } from '../lib/localStorage'
import { Word } from '../lib/statuses'
import { ThemeValue } from '../lib/theme'
import { getInitialCurentGuess } from '../lib/utils'
import { getCurrentWord, getRandomWord } from '../lib/words'
import { getDecodedHashParam, HASH_PARAM_KEY_ID } from '../lib/hashUtils'

export type Difficulty = 3 | 4 | 5 | 6 | 7 | 8 | 9
export type View = 'full' | 'compact'

// All valid difficulty levels
export const VALID_DIFFICULTIES: Difficulty[] = [3, 4, 5, 6, 7, 8, 9]

export type GameState = {
  guesses: Word[]
  currentGuess: Word
  solution: Word
  day: number
  random: number
  lastPlayedTimestamp?: number // Timestamp (ms) when the game was last played
}

export type GameStats = {
  winDistribution: number[]
  gamesFailed: number
  currentStreak: number
  bestStreak: number
  totalGames: number
  successRate: number
}

export type State = {
  id: string
  difficulty: Difficulty
  theme: ThemeValue
  view: View
  info: Record<Difficulty, boolean>
  stats: Record<Difficulty, GameStats>
  game: Record<Difficulty, GameState>
  newFeaturesSeen?: boolean
}

export type Action =
  | { type: 'SET_THEME'; theme: ThemeValue }
  | { type: 'SET_VIEW'; view: View }
  | {
      type: 'SET_DIFFICULTY'
      difficulty: State['difficulty']
    }
  | {
      type: 'SET_INFO'
      difficulty: State['difficulty']
      seen: boolean
    }
  | { type: 'SET_DAY'; difficulty: State['difficulty']; day: number }
  | { type: 'SET_RANDOM'; difficulty: State['difficulty']; random: number }
  | {
      type: 'UPDATE_SOLUTION'
      difficulty: State['difficulty']
      solution: Word
    }
  | {
      type: 'UPDATE_GUESSES'
      difficulty: State['difficulty']
      guesses: Word[]
    }
  | {
      type: 'UPDATE_CURRENT_GUESS'
      difficulty: State['difficulty']
      currentGuess: Word
    }
  | {
      type: 'UPDATE_GAME'
      difficulty: State['difficulty']
      solution?: Word
      guesses?: Word[]
      currentGuess?: Word
    }
  | {
      type: 'UPDATE_STATS'
      difficulty: State['difficulty']
      stats: GameStats
    }
  | {
      type: 'SET_NEW_FEATURES_SEEN'
      seen: boolean
    }
  | {
      type: 'UPDATE_STATE'
      state: Partial<State>
    }

export const getInitialState = (difficulty: Difficulty): GameState => {
  const { solution } = getCurrentWord(0, difficulty)
  return {
    day: 0,
    random: -1,
    solution,
    guesses: [],
    currentGuess: getInitialCurentGuess(difficulty),
  }
}

export const getInitialStats = (difficulty: Difficulty): GameStats => {
  return {
    winDistribution: times(MAX_NUMBER_OF_GUESSES[difficulty], () => 0),
    gamesFailed: 0,
    currentStreak: 0,
    bestStreak: 0,
    totalGames: 0,
    successRate: 0,
  }
}

export const initialState: State = {
  id: createId(),
  difficulty: createDifficulty(),
  theme: 'light',
  view: 'full',
  info: {
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
    8: false,
    9: false,
  },
  stats: {
    3: getInitialStats(3),
    4: getInitialStats(4),
    5: getInitialStats(5),
    6: getInitialStats(6),
    7: getInitialStats(7),
    8: getInitialStats(8),
    9: getInitialStats(9),
  },
  game: {
    3: getInitialState(3),
    4: getInitialState(4),
    5: getInitialState(5),
    6: getInitialState(6),
    7: getInitialState(7),
    8: getInitialState(8),
    9: getInitialState(9),
  },
}

export const gameReducer: Reducer<State, Action> = (state, action): State => {
  switch (action.type) {
    case 'SET_THEME': {
      return { ...state, theme: action.theme }
    }
    case 'SET_VIEW': {
      return { ...state, view: action.view }
    }
    case 'SET_DIFFICULTY': {
      return {
        ...state,
        difficulty: action.difficulty,
      }
    }
    case 'SET_INFO': {
      return {
        ...state,
        info: {
          ...state.info,
          [action.difficulty]: action.seen,
        },
      }
    }
    case 'SET_DAY': {
      return {
        ...state,
        game: {
          ...state.game,
          [action.difficulty]: {
            ...(state.game[action.difficulty] ?? {}),
            solution: getCurrentWord(action.day, state.difficulty).solution,
            day: action.day,
          } as GameState,
        },
      }
    }
    case 'SET_RANDOM': {
      return {
        ...state,
        game: {
          ...state.game,
          [action.difficulty]: {
            ...(state.game[action.difficulty] ?? {}),
            ...(action.random > -1
              ? {
                  solution: getRandomWord(action.random, state.difficulty)
                    .solution,
                }
              : {}),
            random: action.random,
          } as GameState,
        },
      }
    }
    case 'UPDATE_GUESSES': {
      return {
        ...state,
        game: {
          ...state.game,
          [action.difficulty]: {
            ...(state.game[action.difficulty] ?? {}),
            guesses: action.guesses,
            lastPlayedTimestamp: Date.now(),
          } as GameState,
        },
      }
    }
    case 'UPDATE_SOLUTION': {
      return {
        ...state,
        game: {
          ...state.game,
          [action.difficulty]: {
            ...(state.game[action.difficulty] ?? {}),
            solution: action.solution,
            lastPlayedTimestamp: Date.now(),
          } as GameState,
        },
      }
    }
    case 'UPDATE_CURRENT_GUESS': {
      return {
        ...state,
        game: {
          ...state.game,
          [action.difficulty]: {
            ...(state.game[action.difficulty] ?? {}),
            currentGuess: action.currentGuess,
            lastPlayedTimestamp: Date.now(),
          } as GameState,
        },
      }
    }
    case 'UPDATE_STATS': {
      return {
        ...state,
        stats: {
          ...state.stats,
          [action.difficulty]: action.stats,
        },
      }
    }
    case 'SET_NEW_FEATURES_SEEN': {
      return {
        ...state,
        newFeaturesSeen: action.seen,
      }
    }
    case 'UPDATE_GAME': {
      return {
        ...state,
        game: {
          ...state.game,
          [action.difficulty]: {
            ...(state.game[action.difficulty] ?? {}),
            ...omit(action, 'type', 'difficulty'),
            lastPlayedTimestamp: Date.now(),
          },
        },
      }
    }
    case 'UPDATE_STATE': {
      // Check if we're loading from a shared link (URL has user ID)
      const isFromSharedLink = !!getDecodedHashParam(HASH_PARAM_KEY_ID)

      // Merge the incoming state
      const incomingGame = (action.state.game ?? {}) as Partial<
        Record<Difficulty, GameState>
      >

      // If coming from shared link, prefer Firebase data completely
      // Otherwise, merge with existing localStorage data
      const mergedGame: Record<Difficulty, GameState> = isFromSharedLink
        ? ({ ...incomingGame } as Record<Difficulty, GameState>)
        : { ...state.game }

      // Get all difficulties that need checking - both from incoming state and current state
      const allDifficulties: Set<string> = new Set([
        ...Object.keys(incomingGame),
        ...Object.keys(mergedGame),
      ])

      // For each difficulty, check if we need to reset for a new day
      for (const diffKey of Array.from(allDifficulties)) {
        const diff = Number(diffKey) as Difficulty

        // Skip if not a valid difficulty value
        if (!VALID_DIFFICULTIES.includes(diff)) {
          continue
        }

        // Prefer incoming game state from Firebase, fall back to current state
        const savedGame = incomingGame[diff] ?? mergedGame[diff]
        if (savedGame) {
          // Check if we need to reset based on timestamp (works for all game types)
          const now = Date.now()
          const savedTimestamp = savedGame.lastPlayedTimestamp
          let shouldReset = false

          // If no timestamp, it's old data - reset it
          if (!savedTimestamp) {
            shouldReset = true
          }
          // For DAILY games (random === -1), check if it's a different day
          else if (savedGame.random === -1) {
            const savedDate = new Date(savedTimestamp)
            const nowDate = new Date(now)

            // Check if dates differ (compare year, month, day)
            const isDifferentDay =
              savedDate.getFullYear() !== nowDate.getFullYear() ||
              savedDate.getMonth() !== nowDate.getMonth() ||
              savedDate.getDate() !== nowDate.getDate()

            if (isDifferentDay) {
              shouldReset = true
            }
          }
          if (shouldReset) {
            // For daily games, reset to today's word
            if (savedGame.random === -1) {
              const { solution: todaySolution } = getCurrentWord(0, diff)
              mergedGame[diff] = {
                day: 0,
                random: -1,
                solution: todaySolution,
                guesses: [],
                currentGuess: getInitialCurentGuess(diff),
                lastPlayedTimestamp: now,
              }
            } else {
              // For random/custom games, just add timestamp but keep the game
              mergedGame[diff] = {
                ...savedGame,
                lastPlayedTimestamp: now,
              }
            }
          } else {
            // Same day/session, keep the saved game state but update timestamp
            mergedGame[diff] = {
              ...savedGame,
              lastPlayedTimestamp: now,
            }
          }
        }
      }

      return {
        ...state,
        id: action.state.id ?? state.id,
        difficulty: action.state.difficulty ?? state.difficulty,
        theme: action.state.theme ?? state.theme,
        view: action.state.view ?? state.view,
        info: action.state.info ?? state.info,
        game: mergedGame,
        stats: {
          ...state.stats,
          ...(action.state.stats ?? {}),
        },
      }
    }
  }

  return state
}
